import {
  Alert,
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  IconButtonProps,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import DeleteEntityDialog from "components/DeleteEntityDialog";
import SnackbarMessage from "components/SnackbarMessage";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import useTrashablePromises from "hooks/useTrashablePromises";
import React, { useCallback, useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import {
  IMenuStructureRequest,
  ListRequestOfBaseFilter,
  ListResponseOfMenuStructure,
  MenuStructure,
  MenuStructureRequest,
  MenuStructureSequenceRequest,
} from "services/KnowfullyAdminClient";
import { Severity, SnackbarMessageProps } from "types/common-types";
import translateError from "utils/translateError";
import MenuStructureForm from "./MenuStructureForm";
import {
  mapToMenuStructureModels,
  MenuStructureModel,
  menuStructureTableCommonProps,
} from "./menuStructureHelpers";
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_TableInstance,
} from "material-react-table";

type MenuStructureFormProps = {
  menuId: string;
};

const MenuStructuresList = ({ menuId }: MenuStructureFormProps) => {
  const { registerPromise } = useTrashablePromises();
  const { formatMessage: t } = useIntl();
  const queryClient = useQueryClient();
  const { menuService } = useGlobalContext();
  const [deleting, setDeleting] = useState<MenuStructure | null>(null);
  const [editing, setEditing] = useState<MenuStructure | null>(null);
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);
  const [createOpen, createOpenActions] = useBoolean(false);
  const [deleteOpen, deleteOpenActions] = useBoolean(false);
  const [cantDropDialogOpen, cantDropDialogOpenActions] = useBoolean(false);

  const {
    data: menuStructureData,
    isFetching: isFetchingMenuStructures,
  } = useQuery<ListResponseOfMenuStructure>(
    ["menuStructures", menuId],
    () =>
      menuService.getMenuStructures(
        menuId,
        ListRequestOfBaseFilter.fromJS({
          sortField: "sequence",
          sortDescending: false,
        })
      ),
    {
      enabled: !!menuId,
    }
  );

  const menuStructures = mapToMenuStructureModels(menuStructureData?.data);

  const handleDelete = useCallback(async () => {
    const id = deleting?.id;
    setDeleting(null);
    if (id) {
      try {
        await registerPromise(menuService.deleteMenuStructure(id));
        queryClient.invalidateQueries(["menuStructures", menuId]);
        queryClient.invalidateQueries(["parentMenuStructures", menuId]);

        setSnackbarMessage(
          new SnackbarMessageProps(
            t({ id: "menuStructureDeleted" }),
            Severity.Warning
          )
        );

        deleteOpenActions.setFalse();
      } catch (err: any) {
        return { [FORM_ERROR]: translateError(err, t) };
      }
    }
  }, [
    menuId,
    menuService,
    deleteOpenActions,
    deleting?.id,
    queryClient,
    registerPromise,
    t,
  ]);

  const columns: Array<MRT_ColumnDef<MenuStructureModel>> = [
    {
      accessorKey: "name",
      header: t({ id: "certificateNameLabel" }),
    },
    {
      accessorKey: "menuStructureType",
      header: t({ id: "menuStructureTypeLabel" }),
    },
    {
      accessorKey: "menuItem",
      header: t({ id: "menuTypeLabel" }),
    },
    {
      accessorKey: "parentMenuStructure",
      header: t({ id: "parentMenuStructureLabel" }),
    },
    {
      header: " ",
      size: 50,
      enableSorting: false,
      enableResizing: false,
      enableColumnOrdering: false,
      enableHiding: false,
      accessorFn: (row) => row,
      Cell: ({ row }) => (
        <>
          <IconButton
            size="small"
            onClick={() => {
              setEditing(row.original);
              createOpenActions.setTrue();
            }}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => {
              setDeleting(row.original);
              deleteOpenActions.setTrue();
            }}
          >
            <DeleteIcon />
          </IconButton>
        </>
      ),
    },
  ];

  const saveSequence = async (menuStructureId: string, sequence: number) => {
    try {
      await menuService.saveMenuStructureSequence(
        menuId,
        new MenuStructureSequenceRequest({
          menuStructureId,
          sequence,
        })
      );

      queryClient.invalidateQueries(["menuStructures", menuId]);
    } catch (error) {
      return { [FORM_ERROR]: translateError(error, t) };
    }
  };

  const onSubmit = async (values: IMenuStructureRequest) => {
    try {
      const response = await registerPromise(
        menuService.saveMenuStructure(menuId, new MenuStructureRequest(values))
      );

      setSnackbarMessage(
        new SnackbarMessageProps(t({ id: "menuStructureCreateMessage" }))
      );

      queryClient.invalidateQueries(["menuStructures", menuId]);
      queryClient.invalidateQueries(["parentMenuStructures", menuId]);
      createOpenActions.setFalse();

      return response;
    } catch (err: any) {
      return { [FORM_ERROR]: translateError(err, t) };
    }
  };

  const renderCreateMenuStructureButton = () => (
    <Box sx={{ display: "flex", justifyContent: "end", width: "100%" }}>
      <Button
        variant="outlined"
        onClick={() => {
          setEditing(null);
          createOpenActions.setTrue();
        }}
      >
        <T id="menuStructureCreateButtonLabel" values={{ prefix: "Create" }} />
      </Button>
    </Box>
  );

  const handleDragAndDrop = (
    table: MRT_TableInstance<MenuStructureModel>
  ): IconButtonProps => ({
    onDragEnd: async (e) => {
      const { draggingRow, hoveredRow } = table.getState();

      if (!draggingRow?.original.id || !hoveredRow?.original) {
        return;
      }

      if (hoveredRow.depth == draggingRow.depth) {
        await saveSequence(
          draggingRow.original.id,
          hoveredRow.original.sequence
        );
      } else {
        cantDropDialogOpenActions.setTrue();
      }
    },
  });

  return (
    <Container>
      <MaterialReactTable
        {...menuStructureTableCommonProps}
        columns={columns}
        data={menuStructures}
        enableRowOrdering={true}
        enableSorting={false}
        state={{ isLoading: isFetchingMenuStructures, density: "compact" }}
        muiRowDragHandleProps={(e) => handleDragAndDrop(e.table)}
        renderTopToolbarCustomActions={renderCreateMenuStructureButton}
      />

      <DeleteEntityDialog
        dialogTitleKey={"menuStructureDeleteTitle"}
        contentKey={"menuStructureDeleteQuestion"}
        onCancel={deleteOpenActions.setFalse}
        handleDelete={handleDelete}
        open={deleteOpen}
      />
      <Dialog open={createOpen} disableEnforceFocus maxWidth="sm" fullWidth>
        <MenuStructureForm
          menuId={menuId}
          menuStructureId={editing?.id}
          entity={editing}
          onCancel={createOpenActions.setFalse}
          onSubmit={onSubmit}
        />
      </Dialog>
      <Dialog open={cantDropDialogOpen} maxWidth="xs">
        <DialogContent>
          <Alert severity="error">
            {t({ id: "toChangeParentEditMenuStructure" })}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={cantDropDialogOpenActions.setFalse}>
            {t({ id: "okButton" })}
          </Button>
        </DialogActions>
      </Dialog>
      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </Container>
  );
};

export default MenuStructuresList;
